import React from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table'

// If the other file exports only the type:
import type { PendingPaymentTableProps } from './PendingPayment'

// Column helper for type safety
const columnHelper = createColumnHelper<PendingPaymentTableProps>()

// Define columns
const columns = [
  columnHelper.accessor('company_name', {
    header: 'Company Name',
    cell: (info) => (
      <Typography variant='body2' fontWeight='medium'>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('company_id', {
    header: 'Company ID',
    cell: (info) => (
      <Typography variant='body2' fontFamily='monospace'>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('currency', {
    header: 'Currency',
    cell: (info) => (
      <Typography variant='body2' fontWeight='600'>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('wallet_balance', {
    header: 'Wallet Balance ',
    cell: (info) => (
      <Typography variant='body2' fontWeight='600'>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('outstanding_dues', {
    header: 'Outstanding Dues',
    cell: (info) => (
      <Typography variant='body2' fontWeight='600'>
        {info.getValue()}
      </Typography>
    )
  })
]

export default function PendingPaymentTable({
  data
}: {
  data: PendingPaymentTableProps[]
}) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel()
  })

  return (
    <Box sx={{ p: 3 }}>
      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: 'grey.50',
                      py: 2
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                hover
                sx={{
                  '&:nth-of-type(odd)': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} sx={{ py: 2.5 }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Empty state */}
        {table?.getRowModel().rows.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color='text.secondary'>
              No payments found
            </Typography>
          </Box>
        )}
      </TableContainer>
    </Box>
  )
}
